import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { pushTraceLogEvent } from '../Component/BookingJourney/StepComponents/sessionEventPush';

/**
 * Safely extracts a plain string from an axios error or any unknown error shape.
 * Prevents "Converting circular structure to JSON" crashes when the server
 * returns an HTML page, a DOM element, or a nested object.
 */
function safeErrorMsg(error, fallback = 'Unable to extend session. Please try again.') {
  try {
    const data = error?.response?.data;
    // data could be a plain string, an object with .message, or an HTML string / DOM node
    if (typeof data === 'string' && data.length < 300 && !data.includes('Converting circular')) return data;
    if (data && typeof data === 'object' && typeof data.message === 'string') return data.message;
    if (typeof error?.message === 'string' && !error.message.includes('Converting circular')) return error.message;
  } catch {
    // ignore any secondary errors
  }
  return fallback;
}

export const useBookingSession = (onSessionExpired) => {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [countdownStartTime, setCountdownStartTime] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [settings, setSettings] = useState({
    duration_seconds: 300,
    warning_before_seconds: 30,
    extension_duration_seconds: 300,
    max_extensions: 2
  });
  const [extensionsUsed, setExtensionsUsed] = useState(0);
  const [maxExtensionsReached, setMaxExtensionsReached] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [extendError, setExtendError] = useState(null);

  const timerRef = useRef(null);
  const sessionDurationMsRef = useRef(300000);
  const warningLoggedRef = useRef(false);

  // §5.5 — When true (wallet native sheet is active), the interval will NOT fire
  // setShowWarningModal or push the session_warning_shown trace event.
  // warningLoggedRef is also left false so the warning fires correctly once resumed.
  const suppressWarningRef = useRef(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URLOPEN}settings/booking_session`, {
        headers: { "Accept": "application/json" }
      });
      if (response?.data?.success) {
        setSettings(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      console.error("Failed to fetch booking session settings:", error);
    }
    return settings;
  }, [settings]);

  const startSession = useCallback(async () => {
    const fetchedSettings = await fetchSettings();
    const durationMs = fetchedSettings.duration_seconds * 1000;
    sessionDurationMsRef.current = durationMs;
    setCountdownStartTime(Date.now());
    setTimeRemaining(durationMs);
    setExtensionsUsed(0);
    setMaxExtensionsReached(false);
    setShowWarningModal(false);
    warningLoggedRef.current = false;
  }, [fetchSettings]);

  const stopSession = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCountdownStartTime(null);
    setTimeRemaining(0);
    setShowWarningModal(false);
    setExtensionsUsed(0);
    setMaxExtensionsReached(false);
  }, []);

  // Suppress warning modal + trace log (call when wallet native sheet opens)
  const suppressWarning = useCallback(() => {
    suppressWarningRef.current = true;
  }, []);

  // Resume warning modal + trace log (call when wallet payment ends — success or failure)
  const resumeWarning = useCallback(() => {
    suppressWarningRef.current = false;
  }, []);

  // §5.5 — paymentType is optional (null by default).
  // When provided ("gpay" | "apple_pay"), the backend bypasses the max-extensions cap.
  // All existing non-wallet callers omit the argument — no behaviour change.
  const extendSession = useCallback(async (paymentType = null) => {
    setIsExtending(true);
    setExtendError(null);
    try {
      const sessionId = localStorage.getItem("bookingSessionId");
      if (!sessionId) {
        console.warn("extendSession: bookingSessionId not found in localStorage");
        setExtendError("Session ID missing. Please try again.");
        setIsExtending(false);
        return;
      }
      const traceSessionId =
        localStorage.getItem("trace_log_id") ||
        localStorage.getItem("bookingSessionId");

      const payload = {
        slot_session_id: sessionId,
        extension_seconds: settings.extension_duration_seconds,
        trace_session_id: traceSessionId,
        // Only include payment_type when a wallet method is active (§5.5 bypass rule)
        ...(typeof paymentType === "string" ? { payment_type: paymentType } : {}),
      };

      const token = localStorage.getItem("token") || localStorage.getItem("patient_auth_token");
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URLOPEN}booking-session/extend`,
        payload,
        { headers }
      );

      if (response?.data?.success) {
        const newExtensionsUsed =
          response.data.extensions_used || extensionsUsed + 1;
        setExtensionsUsed(newExtensionsUsed);

        pushTraceLogEvent(
          {
            event: "session_extended",
            payload: {
              extension_count: newExtensionsUsed,
              extension_duration_seconds: settings.extension_duration_seconds,
              ...(typeof paymentType === "string" ? { payment_type: paymentType } : {}),
            },
            step_status: "success",
          },
          null,
          localStorage.getItem("trace_log_id") ||
          localStorage.getItem("bookingSessionId"),
        );

        const extensionMs = settings.extension_duration_seconds * 1000;
        sessionDurationMsRef.current += extensionMs;
        setTimeRemaining((prev) => prev + extensionMs);
        setShowWarningModal(false);
        warningLoggedRef.current = false;
      } else {
        // API returned success:false — surface to user
        const msg = response?.data?.message || "Unable to extend session. Please try again.";
        setExtendError(msg);
      }
    } catch (error) {
      console.error("Failed to extend session:", error);
      if (error?.response?.status === 422) {
        setMaxExtensionsReached(true);
        pushTraceLogEvent(
          {
            event: "session_extension_declined",
            payload: {
              extension_count: extensionsUsed + 1,
            },
            step_status: "success",
          },
          null,
          localStorage.getItem("trace_log_id") ||
          localStorage.getItem("bookingSessionId"),
        );
      } else {
        const msg = safeErrorMsg(error);
        setExtendError(msg);
      }
    } finally {
      setIsExtending(false);
    }
  }, [settings, extensionsUsed]);

  const declineExtension = useCallback(() => {
    setShowWarningModal(false);
    pushTraceLogEvent({
      event: "session_extension_declined",
      payload: {
        extension_count: extensionsUsed + 1
      },
      step_status: "success"
    }, null, localStorage.getItem("trace_log_id") || localStorage.getItem("bookingSessionId"));
  }, [extensionsUsed]);

  const resumeSession = useCallback(async (startTime, remainingMs, savedExtensionsUsed = 0) => {
    const fetchedSettings = await fetchSettings();
    sessionDurationMsRef.current = remainingMs;
    setCountdownStartTime(startTime);
    setTimeRemaining(remainingMs);
    setExtensionsUsed(savedExtensionsUsed);
    if (savedExtensionsUsed >= fetchedSettings.max_extensions) {
      setMaxExtensionsReached(true);
    }
  }, [fetchSettings]);

  useEffect(() => {
    if (countdownStartTime && timeRemaining > 0) {
      if (timerRef.current) clearInterval(timerRef.current);

      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - countdownStartTime;
        const remaining = Math.max(0, sessionDurationMsRef.current - elapsed);
        setTimeRemaining(remaining);

        if (remaining <= settings.warning_before_seconds * 1000 && remaining > 0) {
          // §5.5 — skip warning entirely when wallet payment native sheet is active.
          // warningLoggedRef stays false so warning fires correctly after resumeWarning().
          if (!warningLoggedRef.current && !suppressWarningRef.current) {
            if (extensionsUsed >= settings.max_extensions) {
              setMaxExtensionsReached(true);
            }
            setShowWarningModal(true);
            warningLoggedRef.current = true;

            pushTraceLogEvent({
              event: "session_warning_shown",
              payload: {
                warning_before_seconds: settings.warning_before_seconds,
                time_remaining_ms: remaining
              },
              step_status: "success"
            }, null, localStorage.getItem("trace_log_id") || localStorage.getItem("bookingSessionId"));
          }
        }

        if (remaining === 0) {
          clearInterval(timerRef.current);
          if (onSessionExpired) {
            onSessionExpired();
          }
        }
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdownStartTime, showWarningModal, settings, extensionsUsed, onSessionExpired]);

  return {
    timeRemaining,
    countdownStartTime,
    showWarningModal,
    settings,
    extensionsUsed,
    maxExtensionsReached,
    isExtending,
    extendError,
    startSession,
    stopSession,
    extendSession,
    declineExtension,
    suppressWarning,
    resumeWarning,
    resumeSession,
  };
};

export default useBookingSession;
